import { z } from 'zod'
import type { GameEndReason, PlayerIndex } from './state.js'

/**
 * Competition: accounts, Elo rating, head-to-head records.
 * Only games between two human accounts are ranked.
 */

export const USERNAME_PATTERN = /^[\p{L}\p{N}_.-]{3,20}$/u

export const RegisterSchema = z.object({
  username: z.string().trim().regex(USERNAME_PATTERN, 'Pseudo : 3 à 20 lettres, chiffres, « _ », « - » ou « . ».'),
  password: z.string().min(6, 'Mot de passe : 6 caractères minimum.').max(128),
})
export const LoginSchema = z.object({
  username: z.string().trim().min(1).max(20),
  password: z.string().min(1).max(128),
})
export type RegisterInput = z.infer<typeof RegisterSchema>
export type LoginInput = z.infer<typeof LoginSchema>

export interface AuthResponse {
  playerId: string
  name: string
  /** Secret session token: kept on the client side, never shared. */
  token: string
}

export const INITIAL_RATING = 1000

export type Tier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'

export const TIERS: ReadonlyArray<{ tier: Tier; min: number; label: string; icon: string }> = [
  { tier: 'diamond', min: 1400, label: 'Diamant', icon: '💎' },
  { tier: 'platinum', min: 1200, label: 'Platine', icon: '🏆' },
  { tier: 'gold', min: 1050, label: 'Or', icon: '🥇' },
  { tier: 'silver', min: 950, label: 'Argent', icon: '🥈' },
  { tier: 'bronze', min: -Infinity, label: 'Bronze', icon: '🥉' },
]

export function tierFor(rating: number): (typeof TIERS)[number] {
  return TIERS.find((t) => rating >= t.min)!
}

/** Next tier and missing points (null at the top). */
export function nextTier(rating: number): { tier: Tier; label: string; icon: string; missing: number } | null {
  const above = [...TIERS].reverse().find((t) => t.min > rating)
  return above ? { tier: above.tier, label: above.label, icon: above.icon, missing: above.min - rating } : null
}

/** Row of the overall leaderboard. */
export interface LeaderboardEntry {
  rank: number
  playerId: string
  name: string
  rating: number
  games: number
  wins: number
  losses: number
  draws: number
  /** > 0: consecutive wins; < 0: consecutive losses. */
  streak: number
  bestStreak: number
}

/** Finished ranked game. */
export interface MatchSummary {
  gameId: string
  players: [{ id: string; name: string }, { id: string; name: string }]
  /** Index of the winner, null for a draw. */
  winner: PlayerIndex | null
  reason: GameEndReason
  ratingBefore: [number, number]
  ratingAfter: [number, number]
  finishedAt: string
}

/** A player's head-to-head record against an opponent. */
export interface HeadToHead {
  opponentId: string
  opponentName: string
  wins: number
  losses: number
  draws: number
  games: number
  lastPlayedAt: string
  /** Recent results from the player's point of view, most recent first. */
  form: Array<'W' | 'L' | 'D'>
}

/** Rivalry: pair of players who often played each other. */
export interface Rivalry {
  a: { id: string; name: string; wins: number }
  b: { id: string; name: string; wins: number }
  draws: number
  games: number
  lastPlayedAt: string
}

export interface LeaderboardResponse {
  players: LeaderboardEntry[]
  rivalries: Rivalry[]
  recent: MatchSummary[]
}

export interface ProfileResponse {
  player: LeaderboardEntry
  /** Number of ranked players (for "3rd out of 42"). */
  rankedPlayers: number
  recent: MatchSummary[]
  rivals: HeadToHead[]
  /** Record of the logged-in visitor against this player (absent for oneself or without a session). */
  vsViewer?: HeadToHead
}

/** Public invite: who challenges you. */
export interface InviteInfo {
  hostName: string
  hostRating: number
  status: 'waiting' | 'active' | 'finished'
}

export const ChallengeSchema = z.object({ username: z.string().trim().min(1).max(20) })

export interface ChallengeNotice {
  fromName: string
  fromRating: number
  gameId: string
  inviteCode: string
}
